import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, Flame } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { NAV_LINKS, ASSETS, ORG } from "@/lib/constants";
import { InterestDialog } from "@/components/dialogs/InterestDialog";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/10 py-2.5" : "bg-transparent py-4"
      }`}
    >
      <nav className="container-x flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
          <img src={ASSETS.logo} alt="CT Blazers basketball logo" className="h-11 w-auto drop-shadow-[0_2px_12px_rgba(37,99,235,0.4)]" />
          <span className="hidden sm:block font-heading text-2xl tracking-wide leading-none text-white">
            CT <span className="text-blaze-orange">BLAZERS</span>
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-white/70 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-2 right-2 -bottom-0.5 h-0.5 bg-blaze-orange scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/#contact"
            data-testid="nav-contact"
            className="hidden 2xl:inline-flex px-2 py-2 text-xs font-semibold uppercase tracking-wide text-white/70 hover:text-white transition-colors"
          >
            Contact
          </Link>
          <InterestDialog category="join" program="Join CT BLAZERS" testId="nav-join">
            <Button
              data-testid="nav-join-btn"
              className="hidden sm:inline-flex bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full px-5 h-10 shadow-[0_6px_24px_rgba(246,140,31,0.35)] transition-all hover:-translate-y-0.5"
            >
              <Flame size={16} className="mr-1" /> Join CT Blazers
            </Button>
          </InterestDialog>

          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-btn"
                className="xl:hidden text-white hover:bg-white/10"
              >
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink-900 border-white/10 w-[300px] p-0">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex items-center gap-2.5 p-5 border-b border-white/10">
                <img src={ASSETS.logo} alt="CT Blazers logo" className="h-9 w-auto" />
                <span className="font-heading text-xl tracking-wide text-white">CT BLAZERS</span>
              </div>
              <div className="flex flex-col p-3">
                {NAV_LINKS.concat([{ label: "Contact", to: "/#contact" }]).map((l) => (
                  <SheetClose asChild key={l.label}>
                    <Link
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                      className="px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      {l.label}
                    </Link>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Button
                    onClick={() => navigate("/#contact")}
                    data-testid="mobile-join-btn"
                    className="mt-3 bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-11"
                  >
                    Join CT Blazers
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
